import { useQuery } from '@tanstack/react-query';
import type { RecordAssignment } from '@grc/types';
import { apiFetch } from '../../lib/api.js';
import { useOrg } from '../../lib/auth.js';
import {
  EmptyState,
  LoadingPane,
  PageHeader,
  Table,
  formatDate,
} from '../../components/ui.js';
import { NextReview, accessLevelLabel } from './AccessReviewShared.js';
import './records.css';

/** The signed-in member's active software access assignments. */
export function MySoftwareAccessPage() {
  const { org } = useOrg();

  const assignments = useQuery({
    queryKey: ['record-assignments', org.orgId, 'mine', 'software'],
    queryFn: () =>
      apiFetch<{ items: RecordAssignment[] }>(`/orgs/${org.orgId}/record-assignments`, {
        query: { collection: 'software', mine: true },
      }),
  });

  if (assignments.isLoading) return <LoadingPane label="Loading your software access" />;
  const items = assignments.data?.items ?? [];

  return (
    <div>
      <PageHeader
        title="My Software Access"
        subtitle="Software you currently have access to, who approved it and when it is next reviewed. Tell an admin if anything here is wrong or no longer needed."
      />
      {items.length === 0 ? (
        <EmptyState
          title="No software access recorded"
          message="When an admin grants you access to software in the inventory, it appears here."
        />
      ) : (
        <Table
          head={
            <>
              <th>Software</th>
              <th>Vendor</th>
              <th>Access level</th>
              <th>Granted</th>
              <th>Approved by</th>
              <th>Next review</th>
              <th>Notes</th>
            </>
          }
        >
          {items.map((a) => (
            <tr key={a.id}>
              <td style={{ fontWeight: 600 }}>{a.recordName}</td>
              <td>{(a.recordData.vendor as string) || '—'}</td>
              <td>
                {accessLevelLabel(a.accessLevel)}
                {a.accessRole && <span className="cell-muted">{a.accessRole}</span>}
              </td>
              <td style={{ whiteSpace: 'nowrap' }}>{formatDate(a.grantedOn ?? a.assignedAt)}</td>
              <td>{a.approvedByName ?? '—'}</td>
              <td style={{ whiteSpace: 'nowrap' }}>
                <NextReview assignment={a} />
              </td>
              <td>{a.notes || '—'}</td>
            </tr>
          ))}
        </Table>
      )}
    </div>
  );
}
